/**
 * Compliance Drawer Theme
 *
 * Comprehensive theme system for ComplianceDrawer components supporting both
 * dark theme (external) and light theme (internal) variants.
 */

export const complianceTheme = {
  semanticTokens: {
    colors: {
      // Drawer level backgrounds and borders
      'compliance.drawer.bg': { _light: 'white', _dark: 'indigo.800' },
      'compliance.drawer.border': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.200',
      },
      'compliance.drawer.overlay': {
        _light: 'blackAlpha.600',
        _dark: 'blackAlpha.800',
      },

      // Background colors
      'compliance.background.primary': { _light: 'white', _dark: 'indigo.800' },

      // Border colors
      'compliance.border.primary': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.200',
      },
      'compliance.border.secondary': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.200',
      },

      // Text colors
      'compliance.text.tertiary': {
        _light: 'gray.400',
        _dark: 'whiteAlpha.600',
      },

      // Header section
      'compliance.header.bg': { _light: 'gray.50', _dark: 'transparent' },
      'compliance.header.border': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.200',
      },
      'compliance.header.text': { _light: 'gray.800', _dark: 'whiteAlpha.900' },

      // Primary text colors
      'compliance.text.primary': {
        _light: 'gray.900',
        _dark: 'white',
      },
      'compliance.text.secondary': {
        _light: 'gray.700',
        _dark: 'whiteAlpha.700',
      },
      'compliance.text.muted': { _light: 'gray.500', _dark: 'gray.400' },
      'compliance.text.disabled': { _light: 'gray.400', _dark: 'gray.600' },

      // Sidebar backgrounds and borders
      'compliance.sidebar.bg': { _light: 'white', _dark: 'transparent' },
      'compliance.sidebar.border': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.200',
      },
      'compliance.sidebar.header': {
        _light: 'gray.50',
        _dark: 'whiteAlpha.50',
      },
      'compliance.sidebar.highlighted.bg': {
        _light: 'gray.50',
        _dark: 'black',
      },
      'compliance.sidebar.highlighted.border': {
        _light: 'gray.50',
        _dark: 'whiteAlpha.300',
      },

      // Card backgrounds and borders
      'compliance.card.bg': { _light: 'white', _dark: 'whiteAlpha.100' },
      'compliance.card.border': { _light: 'gray.200', _dark: 'whiteAlpha.200' },
      'compliance.card.header.bg': {
        _light: 'gray.50',
        _dark: 'whiteAlpha.50',
      },
      'compliance.card.header.text': {
        _light: 'gray.700',
        _dark: 'whiteAlpha.700',
      },
      'compliance.card.title': {
        _light: 'gray.900',
        _dark: 'whiteAlpha.900',
      },
      // Expanded checklist-activity group card in the document rail (solid, a
      // touch lighter than the sidebar so it reads as an elevated surface).
      'compliance.group.expanded.bg': { _light: 'gray.50', _dark: '#1F1F3B' },

      // Interactive states
      'compliance.hover.bg': { _light: 'gray.100', _dark: 'whiteAlpha.200' },
      'compliance.hover.border': {
        _light: 'gray.300',
        _dark: 'whiteAlpha.300',
      },
      'compliance.active.bg': { _light: 'gray.200', _dark: 'whiteAlpha.300' },
      'compliance.focus.ring': { _light: 'blue.500', _dark: 'blue.300' },

      // Selection states
      'compliance.selected.bg': { _light: 'blue.50', _dark: 'whiteAlpha.200' },
      'compliance.selected.border': {
        _light: 'blue.200',
        _dark: 'whiteAlpha.300',
      },
      'compliance.selected.text': { _light: 'blue.700', _dark: 'blue.200' },

      // Document list states
      'compliance.document.bg': { _light: 'gray.50', _dark: 'whiteAlpha.50' },
      'compliance.document.border': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.200',
      },
      'compliance.document.hover': {
        _light: 'gray.100',
        _dark: 'whiteAlpha.100',
      },
      'compliance.document.selected': {
        _light: 'blue.50',
        _dark: 'whiteAlpha.200',
      },

      // Status colors - maintain accessibility in both themes
      'compliance.status.success': { _light: 'green.500', _dark: 'green.500' },
      'compliance.status.error': { _light: 'red.500', _dark: 'red.500' },
      'compliance.status.warning': {
        _light: 'yellow.500',
        _dark: 'yellow.500',
      },
      'compliance.status.info': { _light: 'blue.500', _dark: 'blue.400' },

      // Icon colors
      'compliance.icon.primary': { _light: 'gray.600', _dark: 'gray.300' },
      'compliance.icon.secondary': { _light: 'gray.400', _dark: 'gray.500' },
      'compliance.icon.success': { _light: 'green.500', _dark: 'green.500' },
      'compliance.icon.error': { _light: 'red.500', _dark: 'red.500' },
      'compliance.icon.warning': { _light: 'yellow.500', _dark: 'yellow.500' },

      // Button colors
      'compliance.button.primary.bg': { _light: 'blue.500', _dark: 'cyan.500' },
      'compliance.button.primary.text': {
        _light: 'white',
        _dark: 'indigo.900',
      },
      'compliance.button.primary.hover': {
        _light: 'blue.600',
        _dark: 'cyan.400',
      },
      'compliance.button.secondary.bg': {
        _light: 'gray.100',
        _dark: 'whiteAlpha.200',
      },
      'compliance.button.secondary.text': {
        _light: 'gray.700',
        _dark: 'whiteAlpha.900',
      },
      'compliance.button.secondary.hover': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.300',
      },

      // Tooltip colors
      'compliance.tooltip.bg': { _light: 'gray.800', _dark: 'black' },
      'compliance.tooltip.text': { _light: 'white', _dark: 'whiteAlpha.900' },
      'compliance.tooltip.border': { _light: 'gray.700', _dark: 'gray.600' },
      'compliance.tooltip.item.hover.bg': {
        _light: 'whiteAlpha.100',
        _dark: 'whiteAlpha.100',
      },

      // Loading states
      'compliance.loading.bg': { _light: 'gray.100', _dark: 'whiteAlpha.100' },
      'compliance.loading.text': { _light: 'gray.500', _dark: 'gray.400' },
      'compliance.spinner.color': { _light: 'blue.500', _dark: 'blue.300' },

      // Tag colors
      'compliance.tag.bg': { _light: 'gray.100', _dark: 'whiteAlpha.100' },
      'compliance.tag.text': { _light: 'gray.700', _dark: 'whiteAlpha.600' },
      'compliance.tag.border': { _light: 'gray.200', _dark: 'whiteAlpha.100' },

      // Checklist-issue chips (category / form) + the current-issue pill and the
      // per-document "Open" link. Raw hex values are intentional here in the
      // theme definition; shared components reference only these token names.
      'compliance.chip.category.bg': {
        _light: 'indigo.50',
        _dark: 'rgba(100, 190, 255, 0.12)',
      },
      'compliance.chip.category.text': {
        _light: 'indigo.700',
        _dark: '#64BEFF',
      },
      'compliance.chip.category.border': {
        _light: 'indigo.100',
        _dark: 'rgba(100, 190, 255, 0.32)',
      },
      'compliance.chip.form.bg': {
        _light: 'purple.100',
        _dark: 'rgba(117, 111, 219, 0.24)',
      },
      'compliance.chip.form.text': { _light: 'purple.700', _dark: '#BAB3E8' },
      'compliance.chip.currentIssue.bg': {
        _light: '#FFF4CC',
        _dark: 'rgba(255, 203, 0, 0.14)',
      },
      'compliance.chip.currentIssue.text': {
        _light: '#8A6D00',
        _dark: '#FFD84D',
      },
      'compliance.link.fg': { _light: 'cyan.700', _dark: '#64BEFF' },

      // PDF viewer specific colors
      'compliance.pdf.bg': { _light: 'gray.100', _dark: 'gray.900' },
      'compliance.pdf.overlay': { _light: 'blue.500', _dark: 'blue.300' },
      'compliance.pdf.highlight': {
        _light: 'rgba(59, 130, 246, 0.4)',
        _dark: 'rgba(59, 130, 246, 0.4)',
      },
      // Red/green field overlays used by the embedded SkySlope drawer when
      // the side panel has classified each field as error or success.
      // Values are Chakra `red.500` (#E53E3E) and `green.500` (#38A169) at
      // 35% alpha — hardcoded as rgba because the `transparentize` helper
      // pathway broke theme resolution in nested-color form.
      'compliance.pdf.highlight.error': {
        _light: 'rgba(229, 62, 62, 0.35)',
        _dark: 'rgba(229, 62, 62, 0.35)',
      },
      // Lighter green (Chakra green.400 #48BB78 at 12% alpha) so passing
      // fields read as a subtle wash rather than a strong fill.
      'compliance.pdf.highlight.success': {
        _light: 'rgba(72, 187, 120, 0.12)',
        _dark: 'rgba(72, 187, 120, 0.12)',
      },
      // Yellow field overlay used by the agent compliance review page to
      // render suggestion/warning fields. Chakra `yellow.500` (#ECC94B) at
      // 50% alpha — same hardcoded-rgba style as the error/success tokens
      // above.
      'compliance.pdf.highlight.warning': {
        _light: 'rgba(236, 201, 75, 0.5)',
        _dark: 'rgba(236, 201, 75, 0.5)',
      },

      // Validation specific colors
      'compliance.validation.tooltip.text.primary': {
        _light: 'whiteAlpha.700',
        _dark: 'whiteAlpha.700',
      },
      'compliance.validation.success.bg': {
        _light: 'green.50',
        _dark: 'green.900',
      },
      'compliance.validation.success.border': {
        _light: 'green.200',
        _dark: 'green.700',
      },
      'compliance.validation.error.bg': { _light: 'red.50', _dark: 'red.900' },
      'compliance.validation.error.border': {
        _light: 'red.200',
        _dark: 'red.700',
      },
      'compliance.validation.warning.bg': {
        _light: 'orange.50',
        _dark: 'orange.900',
      },
      'compliance.validation.warning.border': {
        _light: 'orange.200',
        _dark: 'orange.700',
      },

      // Internal-only debug section colors
      'compliance.debug.bg': { _light: 'purple.50', _dark: 'purple.900' },
      'compliance.debug.border': { _light: 'purple.200', _dark: 'purple.700' },
      'compliance.debug.text': { _light: 'purple.700', _dark: 'purple.200' },
      'compliance.debug.code.bg': {
        _light: 'whiteAlpha.800',
        _dark: 'gray.100',
      },
      'compliance.debug.code.text': { _light: 'green.300', _dark: 'green.800' },
      'compliance.debug.code.text.warning': {
        _light: 'orange.400',
        _dark: 'orange.800',
      },
      'compliance.debug.code.text.error': {
        _light: 'red.300',
        _dark: 'red.800',
      },
      // Debug accent elements (badges, highlights)
      'compliance.debug.accent.bg': {
        _light: 'purple.500',
        _dark: 'purple.400',
      },
      'compliance.debug.accent.text': { _light: 'white', _dark: 'white' },

      // Generic badge backgrounds
      'compliance.badge.success.bg': {
        _light: 'green.500',
        _dark: 'green.500',
      },
      'compliance.badge.error.bg': { _light: 'red.500', _dark: 'red.500' },
      'compliance.badge.info.bg': { _light: 'blue.500', _dark: 'blue.400' },
      'compliance.badge.neutral.bg': {
        _light: 'gray.400',
        _dark: 'gray.500',
      },
      'compliance.badge.text.onSolid': { _light: 'white', _dark: 'white' },

      // Divider colors
      'compliance.divider': { _light: 'gray.200', _dark: 'whiteAlpha.200' },
      'compliance.divider.subtle': {
        _light: 'gray.100',
        _dark: 'whiteAlpha.100',
      },

      // Tab colors (grayscale)
      'compliance.tab.bg': { _light: 'transparent', _dark: 'transparent' },
      'compliance.tab.text': { _light: 'gray.600', _dark: 'whiteAlpha.600' },
      'compliance.tab.hover.bg': { _light: 'gray.100', _dark: 'whiteAlpha.50' },
      'compliance.tab.selected.bg': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.200',
      },
      'compliance.tab.selected.text': {
        _light: 'gray.900',
        _dark: 'white',
      },

      // Form colors
      'compliance.input.bg': { _light: 'white', _dark: 'whiteAlpha.100' },
      'compliance.input.border': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.200',
      },
      'compliance.input.text': { _light: 'gray.900', _dark: 'whiteAlpha.900' },
      'compliance.input.placeholder': { _light: 'gray.400', _dark: 'gray.500' },
      'compliance.input.focus.border': {
        _light: 'blue.500',
        _dark: 'blue.300',
      },

      // Notification colors
      // Info
      'compliance.notification.info.bg': {
        _light: 'blue.50',
        _dark: 'blue.900',
      },
      'compliance.notification.info.text': {
        _light: 'blue.900',
        _dark: 'blue.100',
      },
      'compliance.notification.info.border': {
        _light: 'blue.200',
        _dark: 'blue.700',
      },
      // Warning
      'compliance.notification.warning.bg': {
        _light: 'yellow.200',
        _dark: 'yellow.400',
      },
      'compliance.notification.warning.text': {
        _light: 'yellow.900',
        _dark: 'indigo.900',
      },
      'compliance.notification.warning.border': {
        _light: 'yellow.300',
        _dark: 'yellow.400',
      },
      // Success
      'compliance.notification.success.bg': {
        _light: 'green.50',
        _dark: 'green.900',
      },
      'compliance.notification.success.text': {
        _light: 'green.900',
        _dark: 'green.100',
      },
      'compliance.notification.success.border': {
        _light: 'green.200',
        _dark: 'green.700',
      },
      // Error
      'compliance.notification.error.bg': {
        _light: 'red.50',
        _dark: 'red.900',
      },
      'compliance.notification.error.text': {
        _light: 'red.900',
        _dark: 'red.100',
      },
      'compliance.notification.error.border': {
        _light: 'red.200',
        _dark: 'red.700',
      },

      // Field-level diff colors for AI comparison visualization
      'compliance.diff.improved.bg': { _light: 'green.50', _dark: 'green.900' },
      'compliance.diff.improved.border': {
        _light: 'green.200',
        _dark: 'green.700',
      },
      'compliance.diff.improved.text': {
        _light: 'green.700',
        _dark: 'green.200',
      },
      'compliance.diff.improved.icon': {
        _light: 'green.500',
        _dark: 'green.500',
      },
      'compliance.diff.degraded.bg': { _light: 'red.50', _dark: 'red.900' },
      'compliance.diff.degraded.border': {
        _light: 'red.200',
        _dark: 'red.700',
      },
      'compliance.diff.degraded.text': {
        _light: 'red.700',
        _dark: 'red.200',
      },
      'compliance.diff.degraded.icon': {
        _light: 'red.500',
        _dark: 'red.500',
      },
      'compliance.diff.unchanged.bg': {
        _light: 'gray.50',
        _dark: 'whiteAlpha.50',
      },
      'compliance.diff.unchanged.border': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.200',
      },
      'compliance.diff.unchanged.text': {
        _light: 'gray.700',
        _dark: 'whiteAlpha.700',
      },
      'compliance.diff.row.bg': { _light: 'white', _dark: 'whiteAlpha.50' },
      'compliance.diff.row.hover': {
        _light: 'gray.50',
        _dark: 'whiteAlpha.100',
      },
      'compliance.diff.row.border': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.200',
      },
      'compliance.diff.badge.bg': {
        _light: 'gray.100',
        _dark: 'whiteAlpha.200',
      },
      'compliance.diff.badge.border': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.300',
      },
      'compliance.diff.badge.text': {
        _light: 'gray.700',
        _dark: 'whiteAlpha.800',
      },
    },
  },
};
